import React, { useLayoutEffect, useRef, useState } from 'react';
import { X } from '@fabric-ui/icons';
import { shadowPrimaryBottomSm } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, Chip, chipClasses, List, ListItem, Popper } from '@mui/material';
import { OptionObject } from './types';

const clearIcon = <X />;

export interface MultiSelectTagsProps<T> {
  tags: OptionObject<T>[];
  getTagProps: (params: { index: number }) => any;
  size?: 'xSmall' | 'small' | 'medium';
  showAllTags?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  renderOption?: (option: OptionObject<T>) => React.ReactNode;
  getLabelFunc?: (option: OptionObject<T>) => string;
  isRequired?: boolean;
  renderTagSeparator?: React.ReactNode;
  getValueFunc: (option: OptionObject<T>) => T;
}

export const MultiSelectTags = <T,>({
  tags,
  getTagProps,
  showAllTags,
  disabled,
  renderOption,
  size,
  getLabelFunc,
  isRequired,
  renderTagSeparator,
  getValueFunc,
}: MultiSelectTagsProps<T>) => {
  const [visibleTags, setVisibleTags] = useState(tags.length);
  const [remainingCount, setRemainingCount] = useState(0);
  const moreTagWidth = size === 'xSmall' ? 45 : 50;
  const containerRef = useRef<HTMLDivElement>(null);
  const anchorEl = useRef<HTMLDivElement>(null);
  const [popperOpen, setPopperOpen] = useState(false);

  const hiddenTags = tags.slice(visibleTags);

  useLayoutEffect(() => {
    const calculateTagsLength = () => {
      if (!showAllTags && containerRef.current && containerRef.current.parentElement) {
        const tagWidths = Array.from(containerRef.current?.querySelectorAll(`.${chipClasses.root}`)).map(el => el.clientWidth);
        const parentEl = containerRef.current.parentElement;
        const computedStyle = window.getComputedStyle(parentEl);
        const gap = parseFloat(computedStyle.gap);
        const containerWidth =
          parentEl.clientWidth - (parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)) - gap;
        const borderWidth = 2;
        const inputEl = parentEl.querySelector('.MuiInputBase-input');
        const inputStyle = inputEl ? window.getComputedStyle(inputEl) : null;
        const inputWidth = inputStyle ? parseFloat(inputStyle.minWidth) + parseFloat(inputStyle.marginLeft) : 0;
        if (tagWidths.length > 0) {
          const availableWidth = containerWidth - inputWidth - borderWidth - 10;
          const totalAllTagsWidth = tagWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);

          if (totalAllTagsWidth <= availableWidth) {
            setVisibleTags(tagWidths.length);
            setRemainingCount(0);
            return;
          }

          /* eslint-disable fabric-ui/no-let */
          let tagsCount = 0;
          let totalTagsWidth = 0;

          for (const tagWidth of tagWidths) {
            if (availableWidth - moreTagWidth - gap < totalTagsWidth + tagWidth + gap) {
              break;
            }

            totalTagsWidth += tagWidth;
            tagsCount++;
          }

          setVisibleTags(tagsCount);
          setRemainingCount(tags.length - tagsCount);
        }
      }
    };

    calculateTagsLength();
    const resizeObserver = new ResizeObserver(calculateTagsLength);

    if (containerRef?.current?.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, moreTagWidth, tags]);

  return (
    <Box ref={containerRef} sx={{ display: 'contents' }}>
      {tags.map((option, index) => {
        const tagProps = getTagProps({ index });
        const { key, ...otherProps } = tagProps;
        return (
          <React.Fragment key={key || `tag-${String(getValueFunc(option))}`}>
            {renderTagSeparator}
            <Chip
              label={(renderOption || getLabelFunc)?.(option)}
              {...otherProps}
              sx={{
                ...(!showAllTags && visibleTags <= tags.indexOf(option)
                  ? {
                      visibility: 'hidden',
                      position: 'absolute',
                    }
                  : {}),
              }}
              onDelete={tagProps?.onDelete ?? noop}
              deleteIcon={clearIcon}
              disabled={disabled || (isRequired && tags.length === 1) || option.disabled}
            />
          </React.Fragment>
        );
      })}
      {!showAllTags && remainingCount > 0 && (
        <>
          <Chip
            ref={anchorEl}
            onMouseEnter={() => setPopperOpen(true)}
            onMouseLeave={() => setPopperOpen(false)}
            {...getTagProps({ index: tags.length - 1 })}
            key="remaining-tags-chip"
            onDelete={null}
            disabled={disabled}
            sx={{
              maxWidth: moreTagWidth,
              background: 'var(--color-surface-interactive-secondary-default)',
              borderColor: 'var(--color-border-accent-blueGrey-primary)',
            }}
            label={`+${remainingCount}`}
          />
          <Popper open={popperOpen} anchorEl={anchorEl.current} placement="bottom-start" sx={theme => ({ zIndex: theme.zIndex.tooltip })}>
            <List
              sx={{
                mt: '8px',
                fontSize: size === 'xSmall' ? '12px' : '14px',
                backgroundColor: 'var(--color-surface-elevated-10)',
                boxShadow: shadowPrimaryBottomSm,
                borderRadius: '2px',
              }}>
              {hiddenTags.map(option => (
                <ListItem sx={{ px: '8px', py: '6px' }} key={`hidden-tag-${String(getValueFunc(option))}`}>
                  {(renderOption || getLabelFunc)?.(option)}
                </ListItem>
              ))}
            </List>
          </Popper>
        </>
      )}
    </Box>
  );
};
